import { LiveKitRoom, useTracks, ControlBar, RoomAudioRenderer, GridLayout, useLocalParticipant, useRoomContext, ParticipantTile, useParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Zap, Volume2, Send, X, Bot, Mic, Square, Loader2 } from 'lucide-react';
import Blackboard from './Blackboard';

const serverUrl = 'https://livekit.softextensions.com';
const audioCaptureDefaults = {
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: false,
};

const VirtualClassRoom = ({ token, roomName, onDisconnect, audioEnabled = false, isFullScreen = true, isLecturer = false }) => {
    const [showAiChat, setShowAiChat] = useState(false);
    const [showBlackboard, setShowBlackboard] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [room, setRoom] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!token) {
        return <div>Loading Class...</div>;
    }

    return (
        <LiveKitRoom
            video={false}
            audio={false}
            token={token}
            serverUrl={serverUrl}
            connect={true}
            simulcast={true}
            adaptiveStream={true}
            dynacast={true}
            data-lk-theme="default"
            style={{ height: '100%', width: '100%' }}
            audioCaptureDefaults={audioCaptureDefaults}
            onConnected={(r) => {
                console.log('LiveKitRoom: onConnected fired');
                setRoom(r);
            }}
            onDisconnected={() => {
                console.log('LiveKitRoom: onDisconnected fired');
                setRoom(null);
                onDisconnect();
            }}
            onMediaDeviceFailure={(err) => {
                console.error('Media device failure:', err);
                alert('Media device error: Please check if your camera/microphone are being used by another app or if permissions were revoked.');
            }}
        >
            <div className={`relative h-full w-full flex overflow-hidden ${!isFullScreen ? 'pip-mode-active' : ''}`}>
                {/* Blackboard — left panel */}
                {showBlackboard && isFullScreen && (
                    <Blackboard
                        room={room}
                        isLecturer={isLecturer}
                        onClose={() => setShowBlackboard(false)}
                        isMobile={isMobile}
                    />
                )}

                {/* Main video area — shifts to accommodate open sidebars */}
                <div className={`flex-1 relative transition-all duration-300
                    ${showBlackboard && !isMobile && isFullScreen ? 'ml-[700px]' : ''}
                    ${showAiChat && !isMobile && isFullScreen ? 'mr-[350px]' : ''}
                `}>
                    <ClassroomLayout />
                    {isFullScreen && (
                        <RoomControls
                            onToggleAi={() => setShowAiChat(!showAiChat)}
                            aiActive={showAiChat}
                            onToggleBlackboard={() => setShowBlackboard(!showBlackboard)}
                            blackboardActive={showBlackboard}
                            isMobile={isMobile}
                            isFullScreen={isFullScreen}
                        />
                    )}
                </div>

                {/* AI Tutor — right panel */}
                {showAiChat && isFullScreen && (
                    <AiTeacherSidebar
                        unitName={roomName}
                        onClose={() => setShowAiChat(false)}
                        isMobile={isMobile}
                    />
                )}
            </div>
        </LiveKitRoom>
    );
};

// Custom Video Layout using GridLayout to enforce stability and avoid jumping containers
const ClassroomLayout = () => {
    // Get all camera and screenshare tracks reliably
    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );
    
    // Sort purely by identity to enforce stable UI grids. 
    // This stops the active speaker jumpiness that breaks the UI flow.
    const stableTracks = [...tracks].sort((a, b) => {
        // Screen shares should always float to the top
        if (a.source === Track.Source.ScreenShare) return -1;
        if (b.source === Track.Source.ScreenShare) return 1;
        return a.participant.identity.localeCompare(b.participant.identity);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* The underlying GridLayout automatically handles visual paginations/scaling (4 users = 4 blocks) natively */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                <GridLayout tracks={stableTracks} style={{ height: '100%' }}>
                    <ParticipantTile />
                </GridLayout>
            </div>
            
            {/* The Control Bar must render when possible so custom buttons can portal into it */}
            <div style={{ zIndex: 10 }}>
                <ControlBar controls={{ microphone: true, camera: true, screenShare: true, chat: false, leave: false }} />
            </div>
            
            <RoomAudioRenderer />
        </div>
    );
};

// Hand Raise Control Component
const RoomControls = ({ onToggleAi, aiActive, onToggleBlackboard, blackboardActive, isMobile, isFullScreen }) => {
    const { localParticipant } = useLocalParticipant();
    const [handRaised, setHandRaised] = React.useState(false);
    const [notifications, setNotifications] = React.useState([]);
    const roomContext = useRoomContext();
    const room = roomContext ? roomContext.room : null;
    const [controlBarNode, setControlBarNode] = React.useState(null);

    React.useEffect(() => {
        let interval;
        const findNode = () => {
             const node = document.querySelector('.lk-control-bar');
             if (node !== controlBarNode) {
                 setControlBarNode(node);
             }
        };
        findNode();
        interval = setInterval(findNode, 500);
        return () => clearInterval(interval);
    }, [controlBarNode]);

    React.useEffect(() => {
        if (!room) return;

        const handleData = (payload, participant) => {
            const decoder = new TextDecoder();
            const strData = decoder.decode(payload);
            try {
                const data = JSON.parse(strData);
                if (data.type === 'hand_raise') {
                    showNotification(`${participant.identity} raised hand ✋`);
                }
            } catch (e) {
                console.error('Failed to parse data message', e);
            }
        };

        room.on('dataReceived', handleData);
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room]);

    const showNotification = (msg) => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, msg }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 3000);
    };

    const toggleHand = () => {
        const newState = !handRaised;
        setHandRaised(newState);

        const data = JSON.stringify({ type: 'hand_raise', value: newState });
        const encoder = new TextEncoder();
        localParticipant.publishData(encoder.encode(data), { reliable: true });

        if (newState) {
            showNotification('You raised your hand ✋');
        }
    };

    const btnBase = (active, activeColor) => ({
        padding: isMobile ? '8px 12px' : '10px 18px',
        borderRadius: '12px',
        border: 'none',
        backgroundColor: active ? activeColor : '#1f2937',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: 'bold',
        fontSize: isMobile ? '12px' : '13px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
    });

    const buttonsContent = (
        <div style={{
            display: 'flex',
            gap: '6px',
            marginLeft: controlBarNode ? (isMobile ? '4px' : '12px') : '0',
            alignItems: 'center'
        }}>
            {/* Raise Hand */}
            <button onClick={toggleHand} style={btnBase(handRaised, '#eab308')}>
                <span>✋</span>
                {handRaised ? (isMobile ? 'Down' : 'Lower Hand') : (isMobile ? 'Hand' : 'Raise Hand')}
            </button>

            {/* Blackboard toggle */}
            <button onClick={onToggleBlackboard} style={btnBase(blackboardActive, '#166534')} title="Interactive Blackboard">
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🖊️</span>
                {!isMobile && 'Blackboard'}
            </button>

            {/* AI Tutor */}
            <button onClick={onToggleAi} style={btnBase(aiActive, '#4f46e5')}>
                <Zap className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} ${aiActive ? 'text-yellow-400 fill-yellow-400' : 'text-indigo-400'}`} />
                {isMobile ? 'Tutor' : 'AI Tutor'}
            </button>
        </div>
    );

    return (
        <>
            {controlBarNode ? createPortal(buttonsContent, controlBarNode) : (
                <div style={{ 
                    position: 'absolute', 
                    bottom: isFullScreen ? (isMobile ? '85px' : '20px') : '10px', 
                    left: isFullScreen && isMobile ? '50%' : '10px', 
                    transform: isFullScreen && isMobile ? 'translateX(-50%)' : 'none',
                    zIndex: 100, 
                    display: 'flex',
                    width: isFullScreen && isMobile ? 'max-content' : 'auto'
                }}>
                    {buttonsContent}
                </div>
            )}

            {/* Notification Overlay */}
            <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {notifications.map(n => (
                    <div key={n.id} style={{
                        padding: '12px 20px',
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        color: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(8px)',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        {n.msg}
                    </div>
                ))}
            </div>
        </>
    );
};

const AiTeacherSidebar = ({ unitName, onClose, isMobile }) => {
    const [messages, setMessages] = useState([
        { role: 'assistant', content: `Hello! I'm your Intelligent Learning Assistant for ${unitName}. How can I help you today?` }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [playingId, setPlayingId] = useState(null);
    const scrollRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    message: input,
                    unitName: unitName,
                    history: messages
                })
            });

            const data = await response.json();
            // Assuming data contains choice from DeepSeek
            const aiMsg = { role: 'assistant', content: data.suggestedPlan?.content || data.message || "I'm thinking about that..." };
            setMessages(prev => [...prev, aiMsg]);
        } catch (err) {
            console.error('Tutor Chat Error:', err);
            setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I hit a snag while thinking. Please try again!' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSpeak = async (index, text) => {
        try {
            setPlayingId(index);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/speech/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ text })
            });

            if (!response.ok) throw new Error('Speech failed');

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => setPlayingId(null);
            audio.play();
        } catch (err) {
            console.error('Speech Error:', err);
            setPlayingId(null);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                handleTranscription(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error('Error starting recording:', err);
            alert('Could not access microphone.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleTranscription = async (blob) => {
        setTranscribing(true);
        const formData = new FormData();
        formData.append('audio', blob);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/speech/transcribe`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const data = await response.json();
            if (data.text) {
                setInput(data.text);
                // Optionally auto-send:
                // sendMessage(data.text);
            }
        } catch (err) {
            console.error('Transcription Error:', err);
        } finally {
            setTranscribing(false);
        }
    };

    return (
        <div style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: isMobile ? '100%' : '350px',
            backgroundColor: '#111827',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000
        }}>
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-gray-900">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-indigo-600 rounded-lg">
                        <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-white font-bold text-sm">Intelligent Tutor</h3>
                        <div className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span className="text-[10px] text-gray-400 font-medium">Online</span>
                        </div>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-400" />
                </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, i) => (
                    <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-tr-none'
                            : 'bg-gray-800 text-gray-200 rounded-tl-none border border-white/5'
                            }`}>
                            {msg.content}
                        </div>
                        {msg.role === 'assistant' && (
                            <button
                                onClick={() => handleSpeak(i, msg.content)}
                                disabled={playingId === i}
                                className={`mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${playingId === i ? 'text-green-400' : 'text-indigo-400 hover:text-indigo-300'
                                    }`}
                            >
                                <Volume2 className={`w-3 h-3 ${playingId === i ? 'animate-pulse' : ''}`} />
                                {playingId === i ? 'Speaking...' : 'Listen'}
                            </button>
                        )}
                    </div>
                ))}
                {loading && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs italic">
                        <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></span>
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                        </div>
                        Assistant is processing...
                    </div>
                )}
            </div>

            <form onSubmit={handleSendMessage} className="p-4 bg-gray-900 border-t border-white/10">
                <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={transcribing ? "Transcribing voice..." : "Ask anything..."}
                            className="w-full bg-gray-800 border border-white/10 text-white rounded-xl py-3 pl-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-inner"
                            disabled={transcribing}
                        />
                        {transcribing && (
                            <div className="absolute right-3 top-3">
                                <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`p-3 rounded-xl transition-all ${isRecording
                                ? 'bg-red-600 text-white animate-pulse'
                                : 'bg-gray-800 text-gray-400 hover:text-white border border-white/10'
                            }`}
                        title={isRecording ? "Stop Recording" : "Ask with Voice"}
                    >
                        {isRecording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>

                    <button
                        type="submit"
                        disabled={!input.trim() || loading || transcribing}
                        className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-900/40"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VirtualClassRoom;
